import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import dayjs from "dayjs";
import API from "../../api/axiosInstance";
import { todayLAKey, utcToLADateKey, laDateKeyToUTC } from "../../utils/laDateUtils";
import { LA, DAYS, getDates } from "./roomsInformationUtils";

export function buildBookingsMap(bookings) {
  const map = {};
  for (const b of bookings) {
    const unitId = b.room_unit_id?._id || b.room_unit_id;
    const dateKey =
      b.la_date_key != null && b.la_date_key !== ""
        ? b.la_date_key
        : b.date
          ? typeof b.date === "string"
            ? b.date.slice(0, 10)
            : new Date(b.date).toISOString().slice(0, 10)
          : "";
    const key = `${unitId}|${dateKey}`;
    if (!map[key]) map[key] = [];
    map[key].push(b);
  }
  return map;
}

export function useRoomsInformationData(hotelIdFromUrl, roomIdFromUrl, dateFromUrl) {
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState("");
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [roomUnits, setRoomUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const validDate = dateFromUrl && /^\d{4}-\d{2}-\d{2}$/.test(dateFromUrl);
    return laDateKeyToUTC(validDate ? dateFromUrl : todayLAKey());
  });
  const [loading, setLoading] = useState(false);
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);

  const dates = useMemo(() => getDates(startDate, DAYS), [startDate]);
  const bookingsCacheRef = useRef(new Map());

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const res = await API.get("/ems/hotels");
        const data = Array.isArray(res.data) ? res.data : [];
        setHotels(data);
        const matchHotel =
          hotelIdFromUrl && data.some((h) => String(h._id) === String(hotelIdFromUrl));
        setSelectedHotel(matchHotel ? hotelIdFromUrl : "");
      } catch (err) {
        console.error("Failed to fetch hotels:", err);
      } finally {
        setLoadingHotels(false);
      }
    };
    fetchHotels();
  }, [hotelIdFromUrl]);

  useEffect(() => {
    if (!selectedHotel) {
      setRooms([]);
      setSelectedRoom("");
      setLoadingRooms(false);
      return;
    }
    setLoadingRooms(true);
    const fetchRooms = async () => {
      try {
        const res = await API.get("/ems/rooms");
        const all = Array.isArray(res.data) ? res.data : [];
        const filtered = all.filter(
          (r) =>
            Array.isArray(r.hotel_id) &&
            r.hotel_id.some((h) => String(h?._id || h) === selectedHotel)
        );
        setRooms(filtered);
        if (filtered.length) {
          const matchRoom =
            roomIdFromUrl &&
            filtered.some((r) => String(r._id) === String(roomIdFromUrl));
          setSelectedRoom(matchRoom ? roomIdFromUrl : filtered[0]._id);
        } else setSelectedRoom("");
      } catch (err) {
        console.error("Failed to fetch rooms:", err);
      } finally {
        setLoadingRooms(false);
      }
    };
    fetchRooms();
  }, [selectedHotel, roomIdFromUrl]);

  useEffect(() => {
    if (!selectedRoom) {
      setRoomUnits([]);
      setLoadingUnits(false);
      return;
    }
    setLoadingUnits(true);
    const fetchUnits = async () => {
      try {
        const res = await API.get(`/ems/room-units?room_id=${selectedRoom}`);
        setRoomUnits(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch room units:", err);
      } finally {
        setLoadingUnits(false);
      }
    };
    fetchUnits();
  }, [selectedRoom]);

  const requestWeekBookings = useCallback(
    async (weekDates) => {
      const fromDate = utcToLADateKey(weekDates[0]);
      const toDate = utcToLADateKey(weekDates[weekDates.length - 1]);
      const params = new URLSearchParams({
        room_id: selectedRoom,
        from_date: fromDate,
        to_date: toDate,
      });
      if (selectedHotel) params.set("hotel_id", selectedHotel);

      const res = await API.get(`/ems/room-bookings?${params}`);
      return Array.isArray(res.data) ? res.data : [];
    },
    [selectedRoom, selectedHotel]
  );

  const fetchBookings = useCallback(async () => {
    if (!selectedRoom) {
      setBookings([]);
      return;
    }
    setLoading(true);
    try {
      const result = await requestWeekBookings(dates);
      setBookings(result);
      const weekStartKey = utcToLADateKey(dates[0]);
      const cacheKey = `${selectedRoom}|${selectedHotel}|${weekStartKey}`;
      bookingsCacheRef.current.set(cacheKey, {
        dates,
        bookingsMap: buildBookingsMap(result),
      });
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedRoom, selectedHotel, dates, requestWeekBookings]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const bookingsMap = useMemo(() => buildBookingsMap(bookings), [bookings]);

  // Silently prefetch adjacent weeks into the cache once the current week's
  // real fetch has settled. Background-only: never touches visible state.
  useEffect(() => {
    if (loading || !selectedRoom) return;

    const prefetchDirection = (direction) => {
      const targetStart = dayjs(startDate).tz(LA).add(direction * 7, "day").toDate();
      const targetDates = getDates(targetStart, DAYS);
      const weekStartKey = utcToLADateKey(targetDates[0]);
      const cacheKey = `${selectedRoom}|${selectedHotel}|${weekStartKey}`;
      if (bookingsCacheRef.current.has(cacheKey)) return;

      requestWeekBookings(targetDates)
        .then((result) => {
          bookingsCacheRef.current.set(cacheKey, {
            dates: targetDates,
            bookingsMap: buildBookingsMap(result),
          });
        })
        .catch(() => {});
    };

    prefetchDirection(-1);
    prefetchDirection(1);
  }, [loading, selectedRoom, selectedHotel, startDate, requestWeekBookings]);

  const activeRoomType = rooms.find((r) => r._id === selectedRoom);
  const defaultPrice = activeRoomType?.price || 0;
  const bedCount = activeRoomType?.bed_count || 1;

  const todayLA = useMemo(() => todayLAKey(), []);
  const roomFromKey =
    activeRoomType?.from_la_date_key ??
    (activeRoomType?.from_date ? utcToLADateKey(activeRoomType.from_date) : null);
  const roomToKey =
    activeRoomType?.to_la_date_key ??
    (activeRoomType?.to_date ? utcToLADateKey(activeRoomType.to_date) : null);

  const isOutOfRange = useCallback(
    (date) => {
      const dk = utcToLADateKey(date);
      if (roomFromKey && dk < roomFromKey) return true;
      if (roomToKey && dk > roomToKey) return true;
      return false;
    },
    [roomFromKey, roomToKey]
  );

  const isPastDate = useCallback(
    (date) => utcToLADateKey(date) < todayLA,
    [todayLA]
  );

  const shiftDates = useCallback((direction) => {
    setStartDate((prev) => dayjs(prev).tz(LA).add(direction * 7, "day").toDate());
  }, []);

  const getCachedWeek = useCallback(
    (direction) => {
      const targetStart = dayjs(startDate).tz(LA).add(direction > 0 ? 7 : -7, "day").toDate();
      const targetDates = getDates(targetStart, DAYS);
      const weekStartKey = utcToLADateKey(targetDates[0]);
      const cacheKey = `${selectedRoom}|${selectedHotel}|${weekStartKey}`;
      const hit = bookingsCacheRef.current.get(cacheKey);
      return hit || null;
    },
    [startDate, selectedRoom, selectedHotel]
  );

  return {
    hotels,
    selectedHotel,
    setSelectedHotel,
    rooms,
    selectedRoom,
    setSelectedRoom,
    roomUnits,
    setRoomUnits,
    bookings,
    dates,
    bookingsMap,
    defaultPrice,
    bedCount,
    activeRoomType,
    todayLA,
    roomFromKey,
    roomToKey,
    isOutOfRange,
    isPastDate,
    shiftDates,
    getCachedWeek,
    fetchBookings,
    loading,
    loadingHotels,
    loadingRooms,
    loadingUnits,
  };
}
